import type { OrderStatus, PaymentStatus, OrderChannel } from '../pages/Orders/ordersTypes';
import { OrderStatusEnum, PaymentStatusEnum, OrderChannelEnum } from '../pages/Orders/ordersTypes';

type ApiOrderBoardAddress = {
    line1?: string;
    line2?: string;
    city?: string;
    postalCode?: string;
    notes?: string;
};

type ApiOrderBoardCustomer = {
    name?: string;
    email?: string;
    phone?: string;
    companyName?: string;
    taxId?: string;
    address?: ApiOrderBoardAddress | null;
};

type ApiOrderBoardItem = {
    id?: string;
    name?: string;
    quantity?: number;
    price?: number;
    total?: number;
    modifiers?: string[];
    category?: string;
    station?: string;
    note?: string;
};

type ApiOrderBoardTimelineEntry = {
    at?: string;
    label?: string;
    status?: string;
    note?: string;
};

type ApiOrderBoardDineIn = {
    table?: string;
    covers?: number;
    area?: string;
    waiter?: string;
    seatings?: string;
    checkInAt?: string;
    note?: string;
};

type ApiOrderBoardTakeaway = {
    pickupAt?: string;
    pickupName?: string;
    contactless?: boolean;
    packagingNotes?: string;
};

type ApiOrderBoardNotes = {
    customer?: string;
    kitchen?: string;
    delivery?: string;
    billing?: string;
};

export type ApiOrderBoardOrder = {
    id: string;
    code: string;
    channel: string;
    status: string;
    paymentStatus: string;
    paymentMethod?: string;
    createdAt: string;
    scheduledAt?: string | null;
    total?: number;
    serviceFee?: number;
    items?: ApiOrderBoardItem[];
    customer?: ApiOrderBoardCustomer | null;
    notes?: ApiOrderBoardNotes | null;
    timeline?: ApiOrderBoardTimelineEntry[];
    dineIn?: ApiOrderBoardDineIn | null;
    takeaway?: ApiOrderBoardTakeaway | null;
    tags?: string[];
};

type ApiOrderBoardSummary = {
    total?: number;
    pendingRevenue?: number;
    byStatus?: Record<string, number>;
    byChannel?: Record<string, number>;
};

export type ApiOrderBoardResponse = {
    orders: ApiOrderBoardOrder[];
    summary?: ApiOrderBoardSummary;
};

export async function fetchOrdersBoard(): Promise<ApiOrderBoardResponse> {
    const { apiFetch } = await import('./client');
    const res = await apiFetch('/api/tenant/orders/board');
    if (!res.ok) {
        const message = await safeError(res, 'Impossibile recuperare gli ordini');
        throw new Error(message);
    }
    return res.json();
}

export async function updateOrderStatus(channel: OrderChannel, backendId: string, status: OrderStatus, note?: string): Promise<ApiOrderBoardOrder> {
    const { apiFetch } = await import('./client');
    const payload: { status: OrderStatus; note?: string } = { status };
    if (note && note.trim()) {
        payload.note = note.trim();
    }
    const path = `/api/tenant/orders/${channel.toLowerCase()}/${backendId}/status`;
    const res = await apiFetch(path, {
        method: 'PATCH',
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const message = await safeError(res, 'Impossibile aggiornare lo stato dell\'ordine');
        throw new Error(message);
    }
    return res.json();
}

export type RegisterOrderPaymentPayload = {
    amount: number;
    method: string;
    reference?: string;
    note?: string;
    receivedAt?: string;
};

export async function registerOrderPayment(channel: OrderChannel, backendId: string, payload: RegisterOrderPaymentPayload): Promise<ApiOrderBoardOrder> {
    const { apiFetch } = await import('./client');
    const path = `/api/tenant/orders/${channel.toLowerCase()}/${backendId}/payments`;
    const res = await apiFetch(path, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const message = await safeError(res, "Impossibile registrare l'incasso dell'ordine");
        throw new Error(message);
    }
    return res.json();
}

function isOrderStatus(value: string): value is OrderStatus {
    return (Object.values(OrderStatusEnum) as string[]).includes(value);
}

function isPaymentStatus(value: string): value is PaymentStatus {
    return (Object.values(PaymentStatusEnum) as string[]).includes(value);
}

export function normalizeApiStatus(value?: string): OrderStatus | undefined {
    if (!value) return undefined;
    const upper = value.toUpperCase();
    if (isOrderStatus(upper)) {
        return upper;
    }
    return undefined;
}

export function normalizePaymentStatus(value?: string): PaymentStatus | undefined {
    if (!value) return undefined;
    const upper = value.toUpperCase();
    if (isPaymentStatus(upper)) {
        return upper;
    }
    return undefined;
}

export function normalizeChannel(value?: string): OrderChannel | undefined {
    if (!value) return undefined;
    const upper = value.toUpperCase();
    return (Object.values(OrderChannelEnum) as string[]).includes(upper) ? (upper as OrderChannel) : undefined;
}

async function safeError(res: Response, fallback: string): Promise<string> {
    try {
        const text = await res.text();
        return text || fallback;
    } catch (err) {
        return fallback;
    }
}
